import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import moment from 'moment'

import {
  Button,
  ButtonType,
  Icon,
  Modal,
  ProgressMeter,
  Text
} from '@junglescout/edna'

import { COBALT_DEMO_URL } from 'constants/cobalt'
import { sendCtaClickEvent } from 'helpers/segment'

import { useCsvExportLimitModal } from './CsvExportLimitModalContext'

import CobaltLogo from 'icons/svg/cobalt.svg'

const FEATURE_DISPLAY_NAMES = {
  productDatabaseCsvExport: 'Product Database',
  productTrackerCsvExport: 'Product Tracker',
  keywordScoutCsvExport: 'Keyword Scout',
  rankTrackerCsvExport: 'Rank Tracker'
}

const CloseButton = styled(Icon)`
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
  color: ${props => props.theme.colors.grey600};
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px;
`
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const ProgressCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.theme.colors.grey700};
  white-space: nowrap;
`
const CobaltCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 24px;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #1b1f3b 0%, #2b1d52 100%);
`
const CobaltBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const CobaltLogoImg = styled.img`
  width: 28px;
  height: 28px;
`
const CobaltWordmark = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.white};
`

export const CsvExportLimitModal = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()

  const { feature: initiator, isOpen, close } = useCsvExportLimitModal()
  const featureLimits = useSelector(state => state.globalData.featureLimits)

  const limit = featureLimits?.[initiator]
  const featureName = FEATURE_DISPLAY_NAMES[initiator]

  if (!isOpen || !limit || !featureName) return null

  const { featureLimit, resetsOn } = limit
  const usageAtLimit = featureLimit
  const resetDate = resetsOn
    ? moment(resetsOn).format('MMMM D, YYYY')
    : t('csvExportLimitModal.nextCycle', 'your next billing cycle')

  const handleClose = () => close()

  const handleBookDemo = () => {
    sendCtaClickEvent({
      text: 'Book a demo',
      location: 'CSV Export Limit Modal',
      destination: COBALT_DEMO_URL,
      type: 'link',
      extraProps: { feature: initiator }
    })
    window.open(COBALT_DEMO_URL, '_blank', 'noopener noreferrer')
  }

  return (
    <Modal open={isOpen} onOpenChange={handleClose}>
      <Modal.Content
        width="608px"
        maxWidth="608px"
        ariaTitle={t(
          'csvExportLimitModal.title',
          "You've reached your monthly CSV Download limit"
        )}>
        <Container>
          <Modal.Close>
            <span>
              <CloseButton name="X_CLOSE" />
            </span>
          </Modal.Close>

          <Header>
            <ProgressRow>
              <ProgressMeter
                role="meter"
                value={usageAtLimit}
                max={featureLimit}
                color={colors.primary}
              />
              <ProgressCount>
                {usageAtLimit}/{featureLimit}
              </ProgressCount>
            </ProgressRow>
            <Text variant="headingLg" color="grey900">
              {t(
                'csvExportLimitModal.title',
                "You've reached your monthly CSV Download limit"
              )}
            </Text>
            <Text variant="bodySm" color="grey600">
              {t(
                'csvExportLimitModal.description',
                'Your next {{limit}} {{feature}} downloads will be available on {{date}}.',
                { limit: featureLimit, feature: featureName, date: resetDate }
              )}
            </Text>
          </Header>

          <CobaltCard>
            <CobaltBrand>
              <CobaltLogoImg src={CobaltLogo} alt="Cobalt" />
              <CobaltWordmark>Cobalt</CobaltWordmark>
            </CobaltBrand>
            <Text variant="headingMd" color="white" align="center">
              {t('csvExportLimitModal.cobalt.title', 'Looking for more data?')}
            </Text>
            <Text variant="bodySm" color="grey200" align="center">
              {t(
                'csvExportLimitModal.cobalt.description',
                'Cobalt has the deepest 1P and 3P Amazon data — powering brand performance, campaign optimization and market research.'
              )}
            </Text>
            <Button btnType={ButtonType.PRIMARY_BLUE} onClick={handleBookDemo}>
              {t('csvExportLimitModal.cobalt.cta', 'Book a demo')}
            </Button>
          </CobaltCard>
        </Container>
      </Modal.Content>
    </Modal>
  )
}
